import React, { useEffect, useMemo, useState } from 'react';
import { Minus, Plus, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { staffService } from '../../services/staffService';
import { LocationLine } from '../LocationLine';

const OVERAGE_MSG = 'This client cannot receive overages. Allocate any overage quantities to another client.';

// Viewport-width layout detection (no user-agent sniffing)
const useIsNarrow = () => {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
};

const TierBadge = ({ stop }) => (
  stop.guaranteed_returns ? (
    <Badge className="bg-purple-100 text-purple-700" data-testid={`tier-badge-${stop.order_id}`}>Guaranteed Returns</Badge>
  ) : (
    <Badge variant="outline" className="text-gray-600" data-testid={`tier-badge-${stop.order_id}`}>
      {stop.client_type_name || 'Traditional Wholesale'}
    </Badge>
  )
);

export const PickupReconciliationDialog = ({ open, onOpenChange, stop, routeId, onSubmitted }) => {
  const orderId = stop?.order_id;
  const isGR = !!stop?.guaranteed_returns;
  const isNarrow = useIsNarrow();
  const [lines, setLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !stop) return;
    const existing = {};
    (stop.pickup_reconciliation?.lines || []).forEach((l) => { existing[l.product_id] = l; });
    setLines((stop.order_items || []).map((item) => ({
      product_id: item.product_id,
      product_name: item.product_name,
      ordered: item.quantity,
      delivered: existing[item.product_id] ? existing[item.product_id].delivered_qty : item.quantity,
      reason: existing[item.product_id]?.reason || '',
      overageError: false,
    })));
  }, [open, stop]);

  const flashOverage = (pid) => {
    setLines((prev) => prev.map((l) => l.product_id === pid ? { ...l, overageError: true } : l));
    toast.error(OVERAGE_MSG, { position: 'top-center', duration: 4000 });
  };

  const setDelivered = (pid, rawValue) => {
    const value = Math.floor(Number(rawValue));
    if (Number.isNaN(value) || value < 0) return;
    const line = lines.find((l) => l.product_id === pid);
    if (!line) return;
    if (!isGR && value > line.ordered) {
      flashOverage(pid);
      return; // value above ordered is never committed for non-GR
    }
    setLines((prev) => prev.map((l) => l.product_id === pid ? { ...l, delivered: value, overageError: false } : l));
  };

  const step = (pid, delta) => {
    const line = lines.find((l) => l.product_id === pid);
    if (!line) return;
    setDelivered(pid, line.delivered + delta);
  };

  const setReason = (pid, reason) => {
    setLines((prev) => prev.map((l) => l.product_id === pid ? { ...l, reason } : l));
  };

  const changedLines = useMemo(() => lines.filter((l) => l.delivered !== l.ordered), [lines]);
  const invalidLines = useMemo(() => changedLines.filter((l) => !l.reason.trim()), [changedLines]);
  const hasOverageError = lines.some((l) => l.overageError);
  const canSubmit = lines.length > 0 && invalidLines.length === 0 && !hasOverageError && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await staffService.submitPickupReconciliation(orderId, {
        lines: lines.map((l) => ({
          product_id: l.product_id,
          delivered_qty: l.delivered,
          reason: l.delivered !== l.ordered ? l.reason.trim() : '',
        })),
        route_id: routeId || null,
      });
      toast.success(changedLines.length > 0
        ? `Pickup count recorded — ${changedLines.length} item(s) changed`
        : 'Pickup count recorded — no changes', { position: 'top-center' });
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record pickup count', { position: 'top-center', duration: 4000 });
    } finally {
      setSubmitting(false);
    }
  };

  if (!stop) return null;

  const renderReasonInput = (line, idPrefix) => (
    <div className="mt-2">
      <Input
        value={line.reason}
        onChange={(e) => setReason(line.product_id, e.target.value)}
        placeholder="Reason for change (required)"
        className={`h-11 text-sm ${!line.reason.trim() ? 'border-amber-400' : ''}`}
        data-testid={`${idPrefix}-reason-${orderId}-${line.product_id}`}
      />
      {!line.reason.trim() && (
        <p className="text-xs text-amber-600 mt-1">A reason is required for this change</p>
      )}
    </div>
  );

  const overageChip = (line) => (
    line.delivered > line.ordered ? (
      <Badge className="bg-purple-100 text-purple-700 text-xs" data-testid={`overage-chip-${orderId}-${line.product_id}`}>
        Overage +{line.delivered - line.ordered}
      </Badge>
    ) : line.delivered < line.ordered ? (
      <Badge className="bg-amber-100 text-amber-700 text-xs">Short −{line.ordered - line.delivered}</Badge>
    ) : null
  );

  const overageErrorMsg = (line) => (
    line.overageError && (
      <p className="text-xs text-red-600 mt-1 font-medium" data-testid={`overage-error-${orderId}-${line.product_id}`}>
        {OVERAGE_MSG}
      </p>
    )
  );

  const stepper = (line) => (
    <div className="flex items-center gap-1">
      <Button
        type="button" variant="outline" size="icon"
        className="h-11 w-11 shrink-0"
        onClick={() => step(line.product_id, -1)}
        disabled={line.delivered <= 0}
        data-testid={`qty-minus-${orderId}-${line.product_id}`}
      >
        <Minus className="h-4 w-4" />
      </Button>
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        value={line.delivered}
        onChange={(e) => setDelivered(line.product_id, e.target.value)}
        className="h-11 w-16 text-center font-semibold"
        data-testid={`qty-input-${orderId}-${line.product_id}`}
      />
      <Button
        type="button" variant="outline" size="icon"
        className="h-11 w-11 shrink-0"
        onClick={() => step(line.product_id, 1)}
        disabled={!isGR && line.delivered >= line.ordered}
        data-testid={`qty-plus-${orderId}-${line.product_id}`}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[92vh] overflow-y-auto p-0 gap-0"
        data-testid={`pickup-reconciliation-dialog-${orderId}`}
      >
        <DialogHeader className="px-4 pt-4 pb-2 sm:px-6">
          <DialogTitle className="flex flex-wrap items-center gap-2 text-base sm:text-lg">
            <ClipboardCheck className="h-5 w-5 text-blue-600" />
            Pickup count — {stop.order_number}
            <TierBadge stop={stop} />
          </DialogTitle>
          <DialogDescription className="text-left">
            Count each item as you load it. {isGR
              ? 'This client accepts overages — give a reason for any change.'
              : 'This client cannot receive more than ordered.'}
          </DialogDescription>
          <div className="text-left pt-1">
            <p className="text-sm font-semibold text-gray-900">{stop.client_name}</p>
            <LocationLine
              name={stop.location_name}
              source={stop.location_name_source}
              clientName={stop.client_name}
              address={stop.delivery_address}
              testId={`pickup-location-${orderId}`}
            />
          </div>
        </DialogHeader>

        {/* Desktop table (≥640px) */}
        {!isNarrow && (
        <div className="px-6 pb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 uppercase border-b">
                <th className="text-left py-2">Product</th>
                <th className="text-center py-2 w-20">Ordered</th>
                <th className="text-center py-2 w-44">Delivered</th>
                <th className="text-left py-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.product_id} className="border-b align-top">
                  <td className="py-3 pr-2">
                    <div className="font-medium">{line.product_name}</div>
                    <div className="mt-1 flex gap-1">{overageChip(line)}</div>
                    {overageErrorMsg(line)}
                  </td>
                  <td className="py-3 text-center font-medium text-gray-500">{line.ordered}</td>
                  <td className="py-3">{stepper(line)}</td>
                  <td className="py-3">
                    {line.delivered !== line.ordered ? renderReasonInput(line, 'desktop') : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {/* Mobile stacked cards (<640px) */}
        {isNarrow && (
        <div className="px-4 pb-4 space-y-3">
          {lines.map((line) => (
            <div key={line.product_id} className="rounded-lg border p-3 bg-white" data-testid={`recon-card-${orderId}-${line.product_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-sm">{line.product_name}</div>
                {overageChip(line)}
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-gray-500">Ordered: <span className="font-semibold text-gray-800">{line.ordered}</span></span>
                {stepper(line)}
              </div>
              {overageErrorMsg(line)}
              {line.delivered !== line.ordered && renderReasonInput(line, 'mobile')}
            </div>
          ))}
        </div>
        )}

        {/* Sticky action bar */}
        <div className="sticky bottom-0 bg-white border-t px-4 py-3 sm:px-6 flex items-center justify-between gap-3">
          <div className="text-sm" data-testid={`recon-summary-${orderId}`}>
            {changedLines.length > 0 ? (
              <span className="flex items-center gap-1.5 font-medium text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                {changedLines.length} item{changedLines.length === 1 ? '' : 's'} changed
              </span>
            ) : (
              <span className="text-gray-500">No changes</span>
            )}
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="h-11 px-6 bg-blue-600 hover:bg-blue-700"
            data-testid={`submit-reconciliation-${orderId}`}
          >
            {submitting ? 'Saving…' : 'Submit count'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
